<template>
  <b-card no-body>
    <b-card-header class="d-flex justify-content-between align-items-center flex-wrap">
      <b-card-title class="mb-0">
        거래대금상위요청
      </b-card-title>
      <b-button
        variant="primary"
        :disabled="loading"
        @click="fetchRanks"
      >
        <b-spinner
          v-if="loading || loadingMore"
          small
          class="mr-50"
        />
        새로고침
      </b-button>
    </b-card-header>

    <b-card-body>
      <b-alert
        v-if="errorMessage"
        variant="danger"
        show
      >
        {{ errorMessage }}
      </b-alert>

      <div class="d-flex flex-wrap align-items-end mb-2 trade-rank-filter-row">
        <div class="mr-1 mb-50">
          <label class="form-label d-block mb-25">시장구분</label>
          <b-form-select
            v-model="filters.mrktTp"
            :options="marketOptions"
            style="min-width: 180px;"
          />
        </div>
        <div class="mr-1 mb-50">
          <label class="form-label d-block mb-25">관리종목포함</label>
          <b-form-select
            v-model="filters.mangStkIncls"
            :options="manageOptions"
            style="min-width: 180px;"
          />
        </div>
        <div class="mr-1 mb-50">
          <label class="form-label d-block mb-25">거래소구분</label>
          <b-form-select
            v-model="filters.stexTp"
            :options="exchangeOptions"
            style="min-width: 180px;"
          />
        </div>
        <div class="mb-50">
          <b-button
            variant="outline-primary"
            :disabled="loading"
            @click="fetchRanks"
          >
            조회
          </b-button>
        </div>
      </div>

      <div
        v-if="loading"
        class="text-center py-2"
      >
        <b-spinner />
      </div>

      <template v-else>
        <div class="trade-rank-summary mb-2">
          <div class="summary-box">
            <small class="text-muted d-block">조회건수</small>
            <strong>{{ formatNumber(items.length) }}</strong>
          </div>
          <div class="summary-box">
            <small class="text-muted d-block">시장구분</small>
            <strong>{{ marketLabel(filters.mrktTp) }}</strong>
          </div>
          <div class="summary-box">
            <small class="text-muted d-block">거래소구분</small>
            <strong>{{ exchangeLabel(filters.stexTp) }}</strong>
          </div>
        </div>

        <b-table
          :items="items"
          :fields="fields"
          responsive
          small
          show-empty
          empty-text="조회된 거래대금 상위 종목이 없습니다."
        >
          <template #cell(currentRank)="row">
            {{ formatNumber(row.item.currentRank) }}
          </template>
          <template #cell(previousRank)="row">
            {{ formatNumber(row.item.previousRank) }}
          </template>
          <template #cell(currentPrice)="row">
            <span :class="rateClassBySignedValue(row.item.currentPrice)">{{ formatSignedNumber(row.item.currentPrice) }}</span>
          </template>
          <template #cell(previousDiff)="row">
            <span :class="rateClassBySignedValue(row.item.previousDiff)">{{ formatSignedNumber(row.item.previousDiff) }}</span>
          </template>
          <template #cell(fluctuationRate)="row">
            <span :class="rateClassByValue(row.item.fluctuationRate)">{{ formatRate(row.item.fluctuationRate) }}</span>
          </template>
          <template #cell(sellBid)="row">
            {{ formatSignedNumber(row.item.sellBid) }}
          </template>
          <template #cell(buyBid)="row">
            {{ formatSignedNumber(row.item.buyBid) }}
          </template>
          <template #cell(currentTradeVolume)="row">
            {{ formatNumber(row.item.currentTradeVolume) }}
          </template>
          <template #cell(previousTradeVolume)="row">
            {{ formatNumber(row.item.previousTradeVolume) }}
          </template>
          <template #cell(tradeAmount)="row">
            {{ formatTradeAmountEok(row.item.tradeAmount) }}
          </template>
        </b-table>

        <div class="d-flex justify-content-center mt-2">
          <b-button
            v-if="hasMore"
            variant="outline-primary"
            :disabled="loadingMore"
            @click="loadMore"
          >
            <b-spinner
              v-if="loadingMore"
              small
              class="mr-50"
            />
            더 불러오기
          </b-button>
        </div>
      </template>
    </b-card-body>
  </b-card>
</template>

<script>
import axios from '@axios'
import {
  BAlert,
  BButton,
  BCard,
  BCardBody,
  BCardHeader,
  BCardTitle,
  BFormSelect,
  BSpinner,
  BTable,
} from 'bootstrap-vue'

export default {
  components: {
    BAlert,
    BButton,
    BCard,
    BCardBody,
    BCardHeader,
    BCardTitle,
    BFormSelect,
    BSpinner,
    BTable,
  },
  data() {
    return {
      loading: false,
      loadingMore: false,
      errorMessage: '',
      items: [],
      hasMore: false,
      nextKey: '',
      filters: {
        mrktTp: '000',
        mangStkIncls: '1',
        stexTp: '3',
      },
      marketOptions: [
        { value: '000', text: '전체' },
        { value: '001', text: '코스피' },
        { value: '101', text: '코스닥' },
      ],
      manageOptions: [
        { value: '1', text: '포함' },
        { value: '0', text: '미포함' },
      ],
      exchangeOptions: [
        { value: '3', text: '통합' },
        { value: '1', text: 'KRX' },
        { value: '2', text: 'NXT' },
      ],
      fields: [
        { key: 'currentRank', label: '현재순위' },
        { key: 'previousRank', label: '전일순위' },
        { key: 'stockCode', label: '종목코드' },
        { key: 'stockName', label: '종목명' },
        { key: 'currentPrice', label: '현재가' },
        { key: 'previousDiff', label: '전일대비' },
        { key: 'fluctuationRate', label: '등락률' },
        { key: 'sellBid', label: '매도호가' },
        { key: 'buyBid', label: '매수호가' },
        { key: 'currentTradeVolume', label: '현재거래량' },
        { key: 'previousTradeVolume', label: '전일거래량' },
        { key: 'tradeAmount', label: '거래대금(억)' },
      ],
    }
  },
  created() {
    this.fetchRanks()
  },
  methods: {
    parseApiResponse(response) {
      const payload = response && response.data ? response.data : null
      if (!payload || payload.success !== true) {
        throw new Error(payload && payload.error ? payload.error : '요청 처리 중 오류가 발생했습니다.')
      }
      return payload.data
    },
    async fetchRanks() {
      this.loading = true
      this.errorMessage = ''
      this.hasMore = false
      this.nextKey = ''
      try {
        const data = await this.requestRanks('N', '')
        this.applyResponse(data, false)
      } catch (error) {
        this.items = []
        this.errorMessage = error && error.message ? error.message : '거래대금상위요청 조회에 실패했습니다.'
      } finally {
        this.loading = false
      }
    },
    async loadMore() {
      if (!this.hasMore || !this.nextKey) return
      this.loadingMore = true
      try {
        const data = await this.requestRanks('Y', this.nextKey)
        this.applyResponse(data, true)
      } catch (error) {
        this.errorMessage = error && error.message ? error.message : '거래대금상위요청 추가 조회에 실패했습니다.'
      } finally {
        this.loadingMore = false
      }
    },
    async requestRanks(contYn, nextKey) {
      const response = await axios.post('/api/kiwoom/rank-info/trade-amount-top', {
        mrktTp: this.filters.mrktTp,
        mangStkIncls: this.filters.mangStkIncls,
        stexTp: this.filters.stexTp,
        contYn,
        nextKey,
      })
      return this.parseApiResponse(response)
    },
    applyResponse(data, append) {
      const nextItems = Array.isArray(data.items) ? data.items : []
      this.items = append ? this.items.concat(nextItems) : nextItems
      this.hasMore = !!data.hasMore
      this.nextKey = data.nextKey || ''
    },
    marketLabel(value) {
      const found = this.marketOptions.find(option => option.value === value)
      return found ? found.text : value
    },
    exchangeLabel(value) {
      const found = this.exchangeOptions.find(option => option.value === value)
      return found ? found.text : value
    },
    toNumber(value) {
      if (value === null || value === undefined || value === '') return null
      const normalized = String(value).replace(/,/g, '').replace(/\+/g, '')
      const parsed = Number(normalized)
      return Number.isNaN(parsed) ? null : parsed
    },
    formatNumber(value) {
      const numeric = this.toNumber(value)
      if (numeric === null) return '-'
      return numeric.toLocaleString()
    },
    formatSignedNumber(value) {
      const numeric = this.toNumber(value)
      if (numeric === null) return '-'
      if (numeric > 0) return `+${numeric.toLocaleString()}`
      return numeric.toLocaleString()
    },
    formatRate(value) {
      const numeric = this.toNumber(value)
      if (numeric === null) return '-'
      const prefix = numeric > 0 ? '+' : ''
      return `${prefix}${numeric.toFixed(2)}%`
    },
    formatTradeAmountEok(value) {
      const numeric = this.toNumber(value)
      if (numeric === null) return '-'
      const eok = numeric / 100
      const fractionDigits = eok >= 100 ? 0 : 1
      return eok.toLocaleString(undefined, {
        minimumFractionDigits: fractionDigits,
        maximumFractionDigits: fractionDigits,
      })
    },
    rateClassByValue(value) {
      const numeric = this.toNumber(value)
      if (numeric === null) return 'text-body'
      if (numeric > 0) return 'text-danger'
      if (numeric < 0) return 'text-primary'
      return 'text-body'
    },
    rateClassBySignedValue(value) {
      const numeric = this.toNumber(value)
      if (numeric === null) return 'text-body'
      if (numeric > 0) return 'text-danger'
      if (numeric < 0) return 'text-primary'
      return 'text-body'
    },
  },
}
</script>

<style lang="scss" scoped>
.trade-rank-summary {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 0.75rem;
}

.summary-box {
  padding: 0.75rem 1rem;
  border: 1px solid rgba(34, 41, 47, 0.08);
  border-radius: 0.428rem;
  background: rgba(115, 103, 240, 0.04);
}
</style>
